import type { Post } from '@/@types/post'
import Modal from '@/components/Modal'
import { retrievePost } from '@/utils/fetchers/post'
import { css } from '@emotion/react'
import { CloseIcon } from '@ruguoapp/jike-fe-icons'
import { isEmpty } from 'lodash-es'
import { type HTMLAttributes, useCallback } from 'react'
import PostForm from './PostForm'
import {
	resetPostDefaultFormValue,
	setIsPostFormModalShow,
	setPostDefaultFormValue,
	useIsPostFormModalShow,
	usePostDefaultFormValue,
} from './PostForm/state'
import { toPostFormValue } from './PostForm/utils'

export interface PostFormModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'defaultValue'> {}

export default function PostFormModal({ ...props }: PostFormModalProps) {
	const [isShow, setIsShow] = useIsPostFormModalShow()
	const [defaultFormValue] = usePostDefaultFormValue()
	const isEditMode = !isEmpty(defaultFormValue)

	const handleClose = useCallback(() => {
		setIsShow(false)
		resetPostDefaultFormValue()
	}, [setIsShow])

	return (
		<Modal
			isShow={isShow}
			onClose={handleClose}
			backdrop
			header={
				<>
					<div
						css={css`
							display: flex;
							width: fit-content;
							cursor: pointer;
						`}
						onClick={handleClose}
					>
						<CloseIcon
							css={css`
								width: 24px;
								aspect-ratio: 1;
							`}
						/>
					</div>
					<div
						css={css`
							justify-content: center;
						`}
					>
						{isEditMode ? '编辑' : '发布'}动态
					</div>
					<div />
				</>
			}
			css={css`
				.jk-modal-container {
					overflow: hidden;
					margin-top: 5%;
					height: fit-content;
				}
			`}
		>
			<PostForm defaultValue={defaultFormValue} onSubmit={handleClose} />
		</Modal>
	)
}

export function usePostFormModal() {
	const open = useCallback((idOrPost?: string | Post) => {
		setIsPostFormModalShow(true)
		if (idOrPost) {
			const post = typeof idOrPost === 'string' ? retrievePost(idOrPost) : idOrPost
			if (!post) {
				return
			}
			setPostDefaultFormValue(toPostFormValue(post))
		}
	}, [])

	const close = useCallback(() => {
		setIsPostFormModalShow(false)
		resetPostDefaultFormValue()
	}, [])

	return {
		open,
		close,
	}
}
